import { useCallback, useEffect, useState } from 'react'
import { APP_FONTS, DEFAULT_FONT_ID } from '../lib/fonts'

const STORAGE_KEY = 'phoenix:app-font'
const LINK_ID = 'phoenix-google-fonts'
const PRECONNECT_GOOGLE = 'phoenix-google-fonts-preconnect'
const PRECONNECT_GSTATIC = 'phoenix-google-fonts-gstatic'

// Manages the app-wide font choice. Reads the preference from
// localStorage on mount, applies it via the --app-font-family CSS
// variable on <html> (consumed by the body{} rule in index.css), and
// lazy-loads the chosen Google Font's stylesheet into <head>.
//
// Call this once near the App root — it has no per-component state
// beyond the chosen id, so re-rendering elsewhere isn't an issue.
// SettingsPage uses the same hook to expose the changeFont setter.
export function useAppFont() {
  const [fontId, setFontId] = useState(() => readStored())

  useEffect(() => {
    const font = APP_FONTS.find((f) => f.id === fontId) || APP_FONTS[0]
    const root = document.documentElement

    // CSS variable drives body{} in index.css. Empty string clears
    // the variable so the index.css fallback chain (Cairo + system
    // stack) takes over.
    if (font.stack) {
      root.style.setProperty('--app-font-family', font.stack)
    } else {
      root.style.removeProperty('--app-font-family')
    }

    if (!font.google) return undefined

    // First time we load any Google Font, drop preconnect hints to
    // shave a few hundred ms off the initial font download. They're
    // safe to leave in place permanently.
    ensurePreconnect(PRECONNECT_GOOGLE, 'https://fonts.googleapis.com')
    ensurePreconnect(PRECONNECT_GSTATIC, 'https://fonts.gstatic.com', true)

    // One <link> tag in <head> — we swap its href when the user picks
    // a different font. The previous Google Fonts stylesheet stays
    // *cached* in the browser; only the active <link> changes, which
    // is what tells the renderer which family to use.
    let link = document.getElementById(LINK_ID)
    const href = `https://fonts.googleapis.com/css2?${font.google}&display=swap`
    if (!link) {
      link = document.createElement('link')
      link.id = LINK_ID
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    if (link.getAttribute('href') !== href) {
      link.setAttribute('href', href)
    }
    return undefined
  }, [fontId])

  const changeFont = useCallback((id) => {
    setFontId(id)
    try { localStorage.setItem(STORAGE_KEY, id) } catch { /* private mode etc. */ }
  }, [])

  return { fontId, changeFont, fonts: APP_FONTS }
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v && APP_FONTS.some((f) => f.id === v)) return v
  } catch { /* private mode */ }
  return DEFAULT_FONT_ID
}

function ensurePreconnect(id, href, crossorigin = false) {
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'preconnect'
  link.href = href
  if (crossorigin) link.crossOrigin = 'anonymous'
  document.head.appendChild(link)
}
